// ---------------------------------------------------------------------------
// apiError — pull a user-facing message + ADR-0013 requestId out of an axios
// error.
//
// The developer-API returns a `{ message, requestId }` JSON envelope on error
// (ADR-0013); axios nests it at `error.response.data`. Centralizing the
// extraction here replaces the ad-hoc `error.response?.data?.message` reached
// for (inconsistently) across pages, and lets every surface show the same
// shape — message + a quotable reference id.
//
// Mirrors ui/admin-app + ui/app-vectros-ai's apiError helper in plain JS;
// keep them conceptually in sync until the N=3 shared-lib extraction.
// ---------------------------------------------------------------------------

/**
 * The user-facing message for a caught (axios) error. Prefers the API
 * envelope's `message`, falls back to the JS Error message, then a generic
 * default. Never returns empty.
 *
 * @param {unknown} err - the caught error (axios error, Error, or anything)
 * @param {string} [fallback]
 * @returns {string}
 */
export function getApiErrorMessage(err, fallback = 'Something went wrong. Please try again.') {
  const envelopeMessage = err?.response?.data?.message;
  if (typeof envelopeMessage === 'string' && envelopeMessage.trim()) return envelopeMessage;
  // A 409 with no server message (optimistic-concurrency conflict / duplicate
  // write, ADR-0014) is otherwise rendered as the opaque axios "Request failed
  // with status code 409". Give it an actionable default instead.
  if (err?.response?.status === 409) {
    return 'This changed since you loaded it, or already exists. Refresh and try again.';
  }
  const jsMessage = err?.message;
  if (typeof jsMessage === 'string' && jsMessage.trim()) return jsMessage;
  return fallback;
}

/**
 * Whether a caught (axios) error is an HTTP 409 conflict (optimistic-concurrency
 * version conflict or duplicate-write). Lets a surface special-case it.
 *
 * @param {unknown} err
 * @returns {boolean}
 */
export function isConflictError(err) {
  return err?.response?.status === 409;
}

/**
 * The ADR-0013 requestId from a caught (axios) error, or undefined when the
 * error carries no envelope id (e.g. a network error before any response).
 *
 * @param {unknown} err
 * @returns {string | undefined}
 */
export function getApiRequestId(err) {
  const id = err?.response?.data?.requestId;
  return typeof id === 'string' && id.trim() ? id : undefined;
}
